import React, { FunctionComponent, useEffect, useState } from 'react'
import styles from './DateRangeInput.module.sass'
import Field from '../../../controls/Field/Field'
import ShadowContainer from '../../../etc/popover/ShadowContainer/ShadowContainer'
import DateRangePicker, { DATE_RANGES, DateRange } from '../DateRangePicker/DateRangePicker'
import { DateTuple } from '../DatePicker/DatePicker'
import { getOrdOpt, isDefinedFirst, isDefinedTuple } from '../../../../utils/utils'
import { toReadableDateString } from '../../../../utils/date-utils'
import Popover from '../../../etc/Popover'
import CalendarIcon from 'files/calendar.svg'
import Button from '../../../controls/Button/Button'
import ChevronLeftIcon from 'files/chevron/left.svg'
import ChevronRightIcon from 'files/chevron/right.svg'
import { getTupleEq } from 'fp-ts/Eq'
import { ordDate } from 'fp-ts/Ord'
import * as T from 'fp-ts/Tuple'
import { addDays, addYears, endOfMonth, startOfMonth, subDays, subYears } from 'date-fns/fp'
import { differenceInDays } from 'date-fns/esm/fp'
import { flow, pipe } from 'fp-ts/function'
import clsx from 'clsx'

const eqDateTuple = getTupleEq(getOrdOpt(ordDate), getOrdOpt(ordDate))

interface Props {
    className?: string,
    value: DateTuple,
    onChange: (value: DateTuple) => void,
    placeholder?: string,
    navigation?: boolean,
}

const DateRangeInput: FunctionComponent<Props> = props => {
    const [open, setOpen] = useState(false)
    const [range, setRange] = useState<DateRange>('unknown')

    const getValue = () => {
        if (props.value && isDefinedFirst(props.value)) {
            return toReadableDateString(props.value[0]) + ' - ' + toReadableDateString(props.value[1])
        }
    }

    useEffect(() => {
        if (isDefinedTuple(props.value)) {
            const now = props.value[0]
            if (eqDateTuple.equals(DATE_RANGES.year.getDates(now), props.value)) {
                setRange('year')
            } else {
                if (eqDateTuple.equals(DATE_RANGES.month.getDates(now), props.value)) {
                    setRange('month')
                } else {
                    setRange('unknown')
                }
            }
        }
    }, [props.value])

    const change = (operation: 'add' | 'sub') => {
        if (isDefinedTuple(props.value)) {
            const diff = Math.abs(differenceInDays(props.value[0])(props.value[1]))
            let startCalculation = operation === 'add' ? addDays(diff) : subDays(diff)
            let endCalculation = startCalculation
            switch (range) {
                case 'year': {
                    startCalculation = endCalculation = operation === 'add' ? addYears(1) : subYears(1)
                    break
                }
                case 'month': {
                    startCalculation = flow(
                        operation === 'add' ? addDays(32) : subDays(16),
                        startOfMonth,
                    )
                    endCalculation = flow(
                        operation === 'add' ? addDays(16) : subDays(32),
                        endOfMonth,
                    )
                    break
                }
            }
            props.onChange(pipe(
                props.value,
                T.bimap(endCalculation, startCalculation),
            ))
        }
    }

    return <Popover
        isOpen={open}
        positions={['bottom', 'top']}
        onClickOutside={() => setOpen(false)}
        content={state => <ShadowContainer state={state}>
            <DateRangePicker
                value={props.value}
                onChange={value => {
                    props.onChange?.(value)
                    if (isDefinedTuple(value)) {
                        setOpen(false)
                    }
                }}/>
        </ShadowContainer>}
    >
        <div className={clsx(props.className, styles.container)}>
            {
                props.navigation && <Button
                    disabled={!isDefinedTuple(props.value)}
                    onClick={() => change('sub')}
                >
                    <ChevronLeftIcon/>
                </Button>
            }
            <Field
                className={styles.input}
                placeholder={props.placeholder}
                value={getValue()}
                onFocus={() => setOpen(true)}
                rightItem={<CalendarIcon/>}
            />
            {
                props.navigation && <Button
                    disabled={!isDefinedTuple(props.value)}
                    onClick={() => change('add')}
                >
                    <ChevronRightIcon/>
                </Button>
            }
        </div>
    </Popover>
}

export default DateRangeInput